import { Injectable, WritableSignal, signal } from '@angular/core';
import { Router } from '@angular/router';
import { INotification } from 'src/app/common-components/services/common-notification-system.service';
import { NotificationSystemService } from 'src/app/common-components/services/common-notification-system.service';
import { Pages, Roles } from 'src/app/constants/app-types';
import { AdminApprovalService } from 'src/app/services/admin/admin-approval.service';
import { AttendantProfileService } from 'src/app/services/attendant/attendant-profile.service';
import { Attendant, ReviewRequest } from 'src/app/services/providerdirectoryapi/providerdirectoryfunctionapi.services';

@Injectable()
export class AppbarService {
  public currentPage: Pages = 'home';

  public attendantData: WritableSignal<Attendant> = signal<Attendant>(undefined);
  private _adminTrigger: WritableSignal<string> = signal<string>('');
  private _roleTrigger: WritableSignal<Roles> = signal<Roles>('User');
  private _pageTrigger: WritableSignal<Pages> = signal<Pages>('home');
  public reviewRequests: WritableSignal<ReviewRequest[]> = signal<ReviewRequest[]>(undefined);
  public reviewed: WritableSignal<boolean> = signal<boolean>(false);
  public statusPic: WritableSignal<ReviewRequest> = signal<ReviewRequest>(undefined);
  public statusAboutMe: WritableSignal<ReviewRequest> = signal<ReviewRequest>(undefined);
  public statusSnippet: WritableSignal<ReviewRequest> = signal<ReviewRequest>(undefined);
  public statusSelections: WritableSignal<{
    photo: boolean | null;
    about: boolean | null;
    snippet: boolean | null;
    all: boolean | null;
  }> = signal({
    photo: null,
    about: null,
    snippet: null,
    all: null,
  });

  constructor(
    private readonly notificationSystemService: NotificationSystemService,
    private readonly router: Router,
    private readonly attendantProfileService: AttendantProfileService,
    private readonly adminApprovalService: AdminApprovalService
  ) {
    this.getSignals();
  }

  private getSignals() {
    this.attendantData = this.attendantProfileService.fetchPublicData(false);
    this.reviewRequests = this.adminApprovalService.getRequestsByID(undefined, false);
    this.statusPic = this.adminApprovalService.getStatusPicSignal();
    this.statusAboutMe = this.adminApprovalService.getStatusAboutMeSignal();
    this.statusSnippet = this.adminApprovalService.getStatusSnippetSignal();
    this.statusSelections = this.adminApprovalService.getStatusSelectionsSignal();
    this.reviewed = this.adminApprovalService.getReviewedSignal();
  }

  getPageTrigger(): WritableSignal<Pages> {
    return this._pageTrigger;
  }

  getRoleTrigger(): WritableSignal<Roles> {
    return this._roleTrigger;
  }

  getAdminTrigger(): WritableSignal<string> {
    return this._adminTrigger;
  }

  complete() {
    this.adminApprovalService.setRequests([]);
    if (
      this.statusSelections()['photo'] !== null &&
      this.statusSelections()['photo'] !== undefined &&
      this.statusPic()
    ) {
      this.setReviewRequest(
        0,
        this.statusSelections()['photo'] ? 1 : 2,
        this.statusPic().requestId,
        this.attendantData()?.photoUrl
      );
    }
    if (
      this.statusSelections()['about'] !== null &&
      this.statusSelections()['about'] !== undefined &&
      this.statusAboutMe()
    ) {
      this.setReviewRequest(
        2,
        this.statusSelections()['about'] ? 1 : 2,
        this.statusAboutMe().requestId,
        this.attendantData()?.about
      );
    }
    if (
      this.statusSelections()['snippet'] !== null &&
      this.statusSelections()['snippet'] !== undefined &&
      this.statusSnippet()
    ) {
      this.setReviewRequest(
        3,
        this.statusSelections()['snippet'] ? 1 : 2,
        this.statusSnippet().requestId,
        this.attendantData()?.snippet
      );
    }

    if (
      this.statusSelections()['photo'] !== false &&
      this.statusSelections()['about'] !== false &&
      this.statusSelections()['snippet'] !== false
    ) {
      this.attendantProfileService.updateAttendantField('status', 'Approved');
      this.attendantProfileService.updateAttendantField('approvalDate', new Date());
    } else if (
      this.statusSelections()['photo'] === false &&
      this.statusSelections()['about'] === false &&
      this.statusSelections()['snippet'] === false
    ) {
      this.attendantProfileService.updateAttendantField('status', 'Rejected');
      this.attendantProfileService.updateAttendantField('approvalDate', null);
    } else {
      this.attendantProfileService.updateAttendantField('status', 'Reviewed');
      this.attendantProfileService.updateAttendantField('approvalDate', null);
    }
    this.attendantProfileService.update(this.attendantData(), false);
    if (this.reviewRequests().length > 0 && !this.reviewed()) {
      this.adminApprovalService.updateReviewRequest();
      this.adminApprovalService.setRequests([]);
    }
    const notification: INotification = { type: 'success', content: 'Profile Review Complete' };
    this.notificationSystemService.setNotification(notification);
    this.router.navigate(['/Admin/Attendants']);
    this.clear();
  }
  cancel() {
    const notification: INotification = { type: 'success', content: 'Profile Review Canceled' };
    this.notificationSystemService.setNotification(notification);
    this.router.navigate(['/Admin/Attendants']);
    this.clear();
  }
  delete() {
    const notification: INotification = { type: 'success', content: 'Profile Status set as Deleted' };
    this.notificationSystemService.setNotification(notification);
    this.attendantProfileService.updateAttendantField('status', 'Deleted');
    this.attendantProfileService.update(undefined, false);
    this.router.navigate(['/Admin/Attendants']);
    this.clear();
  }
  spam() {
    const notification: INotification = { type: 'success', content: 'Profile Status set as Spam' };
    this.notificationSystemService.setNotification(notification);
    this.attendantProfileService.updateAttendantField('status', 'Spam');
    this.attendantProfileService.update(undefined, false);
    this.router.navigate(['/Admin/Attendants']);
    this.clear();
  }
  refresh() {
    this._adminTrigger.set('refresh');
  }
  clear() {
    this._adminTrigger.set('');
  }

  private setReviewRequest(type: number, status: number, requestId: number, data?: string) {
    const newReq = new ReviewRequest();
    newReq.requestId = requestId;
    newReq.type = type;
    newReq.metadata = data;
    newReq.status = status;
    newReq.revieweeId = this.attendantData()?.userId;
    this.reviewRequests();
    //newReq.reviewerId = this.userService.getIdNow();
    this.adminApprovalService.updateRequest(newReq);
  }

  setPage(str?: Pages) {
    this._adminTrigger.set(null);
    if (str === undefined) {
      str = this.currentPage;
    }
    this._pageTrigger.set(str);
    this.currentPage = str;
  }

  setRole(str: Roles) {
    this._roleTrigger.set(str);
  }
}
